"use client";

import type { Id } from "@convex/_generated/dataModel";
import Image from "next/image";
import NextLink from "next/link";
import { formatNumber } from "@/lib/utils";

const CLASS_IMAGES: Record<string, string> = {
  assault: "/battlefield-6-assault-class-16x9.avif",
  engineer: "/battlefield-6-engineer-class-16x9.avif",
  recon: "/battlefield-6-recon-class-16x9.avif",
  support: "/battlefield-6-support-class-16x9.avif",
};

interface LoadoutCardProps {
  loadout: {
    _id: Id<"loadouts">;
    _creationTime: number;
    name: string;
    description?: string;
    isPublic: boolean;
    slug: string;
    user: {
      _id: Id<"users">;
      name: string;
      pictureUrl?: string;
      username?: string;
    };
    class: {
      _id: Id<"classes">;
      name: string;
      logoUrl: string | null;
    };
    fieldSpec: {
      _id: Id<"fieldSpecs">;
      name: string;
      logoUrl: string | null;
    };
    primaryWeapon: {
      _id: Id<"weapons">;
      name: string;
      category: string;
      logoUrl: string | null;
      imageUrl: string | null;
    };
    secondaryWeapon: {
      _id: Id<"weapons">;
      name: string;
      category: string;
      logoUrl: string | null;
      imageUrl: string | null;
    };
    gadget1: {
      _id: Id<"gadgets">;
      name: string;
      logoUrl: string | null;
    };
    gadget2: {
      _id: Id<"gadgets">;
      name: string;
      logoUrl: string | null;
    };
    throwable: {
      _id: Id<"throwables">;
      name: string;
      logoUrl: string | null;
    };
    stats: {
      upvotes: number;
      downvotes: number;
      comments: number;
      views?: number;
      avgRating: number;
      totalRatings: number;
    };
  };
  featured?: boolean;
}

export function LoadoutCard({ loadout, featured = false }: LoadoutCardProps) {
  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  const classImage =
    CLASS_IMAGES[loadout.class.name.toLowerCase()] ?? CLASS_IMAGES.assault;
  const chips = [
    { id: loadout.secondaryWeapon._id, name: loadout.secondaryWeapon.name },
    { id: loadout.gadget1._id, name: loadout.gadget1.name },
    { id: loadout.gadget2._id, name: loadout.gadget2.name },
    { id: loadout.throwable._id, name: loadout.throwable.name },
  ];

  return (
    <NextLink
      className="group relative flex flex-col overflow-hidden rounded-[8px] border border-[#262A2D] bg-[#141618] transition-colors hover:border-[#F5A23E]/50"
      href={`/loadouts/${loadout.slug}`}
    >
      {/* Background class image */}
      <div className="absolute inset-0">
        <Image
          alt={loadout.class.name}
          className="object-cover object-center opacity-40 transition-opacity duration-300 group-hover:opacity-25"
          fill
          sizes="(max-width: 640px) 100vw, 50vw"
          src={classImage}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#141618]/70 to-[#141618]" />
      </div>

      {/* Primary weapon render */}
      {loadout.primaryWeapon.imageUrl ? (
        <div className="pointer-events-none absolute right-0 bottom-0 h-1/2 w-1/2">
          <Image
            alt={loadout.primaryWeapon.name}
            className="object-contain object-right-bottom opacity-70 drop-shadow-[0_4px_12px_rgba(0,0,0,0.7)] transition-opacity duration-300 group-hover:opacity-90"
            fill
            sizes="(max-width: 640px) 50vw, 25vw"
            src={loadout.primaryWeapon.imageUrl}
          />
        </div>
      ) : null}

      <div className="relative flex flex-1 flex-col gap-3 p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            {loadout.class.logoUrl ? (
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[6px] border border-[#46C8E0]/30 bg-[#0C0D0E]/70 p-1.5">
                <Image
                  alt={loadout.class.name}
                  className="h-full w-full object-contain"
                  height={24}
                  src={loadout.class.logoUrl}
                  width={24}
                />
              </div>
            ) : null}
            <div>
              <div className="font-mono text-[#46C8E0] text-[10px] tracking-[0.15em]">
                {`${loadout.class.name.toUpperCase()} // ${loadout.fieldSpec.name.toUpperCase()}`}
              </div>
              <h3 className="mt-0.5 font-bold font-heading text-[#EAEBEC] text-lg leading-tight">
                {loadout.name}
              </h3>
            </div>
          </div>
          {featured ? (
            <span className="shrink-0 rounded-[3px] bg-[#46C8E0] px-1.5 py-0.5 font-bold font-mono text-[#0a0b0c] text-[10px]">
              FEATURED
            </span>
          ) : null}
        </div>

        <div className="flex items-center gap-2 font-mono text-[#8B9297] text-xs tracking-wider">
          {loadout.primaryWeapon.name}
          <span className="rounded-[3px] bg-[#46C8E0] px-1.5 py-0.5 font-bold text-[#0a0b0c] text-[10px]">
            {loadout.primaryWeapon.category}
          </span>
        </div>

        {loadout.description ? (
          <p className="line-clamp-2 text-[#8B9297] text-sm">
            {loadout.description}
          </p>
        ) : null}

        <div className="mt-auto flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <span
              className="rounded-[3px] border border-[#262A2D] bg-[#0C0D0E]/80 px-1.5 py-0.5 font-mono text-[#A9AFB3] text-[10px]"
              key={chip.id}
            >
              {chip.name}
            </span>
          ))}
        </div>

        <div className="flex items-center justify-between border-[#262A2D] border-t pt-3 font-mono text-xs">
          <div className="flex gap-3 text-[#8B9297]">
            <span className="text-[#2ec27e]">
              ▲ {formatNumber(loadout.stats.upvotes)}
            </span>
            <span>◎ {loadout.stats.comments}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#2b6cb0] text-[9px] text-white">
              {getInitials(loadout.user.name)}
            </span>
            <span className="text-[#8B9297] text-[11px]">
              {loadout.user.username ?? loadout.user.name}
            </span>
          </div>
        </div>
      </div>
    </NextLink>
  );
}
